import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Bar, Timeframe } from '../core/types'
import { TF_SECONDS } from '../core/types'

// IndexedDB-backed dataset store. Imported OHLC survives reloads and works
// offline. One record holds a whole base-timeframe series (small enough:
// tens of thousands of bars). Higher timeframes are resampled on read.

export interface DatasetMeta {
  id: string
  symbol: string
  baseTf: Timeframe
  source: string
  firstTime: number
  lastTime: number
  barCount: number
  createdAt: number
}

interface BacktestDB extends DBSchema {
  datasets: { key: string; value: DatasetMeta }
  bars: { key: string; value: { datasetId: string; bars: Bar[] } }
  settings: { key: string; value: { key: string; value: unknown } }
}

const DB_NAME = 'backtest'
const DB_VERSION = 1

let dbp: Promise<IDBPDatabase<BacktestDB>> | null = null
function db() {
  if (!dbp) {
    dbp = openDB<BacktestDB>(DB_NAME, DB_VERSION, {
      upgrade(d) {
        d.createObjectStore('datasets', { keyPath: 'id' })
        d.createObjectStore('bars', { keyPath: 'datasetId' })
        d.createObjectStore('settings', { keyPath: 'key' })
      },
    })
  }
  return dbp
}

/** Median bar spacing -> nearest supported timeframe. */
export function detectBaseTimeframe(bars: Bar[]): Timeframe {
  if (bars.length < 2) return 'm1'
  const deltas: number[] = []
  for (let i = 1; i < bars.length; i++) deltas.push(bars[i]!.time - bars[i - 1]!.time)
  deltas.sort((a, b) => a - b)
  const median = deltas[Math.floor(deltas.length / 2)]!
  let best: Timeframe = 'm1'
  let bestDiff = Infinity
  for (const tf of Object.keys(TF_SECONDS) as Timeframe[]) {
    const diff = Math.abs(TF_SECONDS[tf] - median)
    if (diff < bestDiff) {
      bestDiff = diff
      best = tf
    }
  }
  return best
}

export interface DatasetGateway {
  init(): Promise<void>
  saveDataset(input: { symbol: string; source: string; bars: Bar[]; baseTf?: Timeframe }): Promise<DatasetMeta>
  listDatasets(): Promise<DatasetMeta[]>
  getActive(): Promise<{ meta: DatasetMeta; bars: Bar[] } | null>
  setActive(id: string): Promise<void>
  deleteDataset(id: string): Promise<void>
  getDrawings(datasetKey: string): Promise<unknown[]>
  saveDrawings(datasetKey: string, list: unknown[]): Promise<void>
}

export function createDatasetGateway(): DatasetGateway {
  return {
    async init() {
      await db()
    },

    async saveDataset({ symbol, source, bars, baseTf }) {
      const d = await db()
      const tf = baseTf ?? detectBaseTimeframe(bars)
      const id = `${symbol}-${tf}-${bars[0]?.time ?? 0}-${bars.at(-1)?.time ?? 0}`
      const meta: DatasetMeta = {
        id,
        symbol,
        baseTf: tf,
        source,
        firstTime: bars[0]?.time ?? 0,
        lastTime: bars.at(-1)?.time ?? 0,
        barCount: bars.length,
        createdAt: Math.floor(nowMs() / 1000),
      }
      const tx = d.transaction(['datasets', 'bars', 'settings'], 'readwrite')
      await tx.objectStore('datasets').put(meta)
      await tx.objectStore('bars').put({ datasetId: id, bars })
      await tx.objectStore('settings').put({ key: 'activeDatasetId', value: id })
      await tx.done
      return meta
    },

    async listDatasets() {
      const d = await db()
      return d.getAll('datasets')
    },

    async getActive() {
      const d = await db()
      const active = await d.get('settings', 'activeDatasetId')
      const id = (active?.value as string | undefined) ?? undefined
      const meta = id ? await d.get('datasets', id) : undefined
      if (!meta) return null
      const rec = await d.get('bars', meta.id)
      if (!rec) return null
      return { meta, bars: rec.bars }
    },

    async setActive(id) {
      const d = await db()
      await d.put('settings', { key: 'activeDatasetId', value: id })
    },

    async deleteDataset(id) {
      const d = await db()
      const tx = d.transaction(['datasets', 'bars'], 'readwrite')
      await tx.objectStore('datasets').delete(id)
      await tx.objectStore('bars').delete(id)
      await tx.done
    },

    // Drawings live in the generic `settings` store, keyed per dataset.
    async getDrawings(datasetKey) {
      const d = await db()
      const rec = await d.get('settings', `drawings:${datasetKey}`)
      return (rec?.value as unknown[] | undefined) ?? []
    },

    async saveDrawings(datasetKey, list) {
      const d = await db()
      await d.put('settings', { key: `drawings:${datasetKey}`, value: list })
    },
  }
}

// `new Date()`/Date.now wrapper isolated for clarity.
function nowMs(): number {
  return Date.now()
}
